import FullComposeInfoBar from './FullComposeInfoBar';
import FullComposeLeftPane, { FullComposeLeftPaneHandle } from './FullComposeLeftPane';
import FullComposeRightPane from './FullComposeRightPane';
import TrackingSidePane from './TrackingSidePane';
import getComposeCommandBarItems from '../utils/getComposeCommandBarItems';
import toggleEmojiPicker from '../utils/toggleEmojiPicker';
import { FocusTrapZone } from '@fluentui/react/lib/FocusTrapZone';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { observer } from 'mobx-react-lite';
import { isConnectedAccount } from 'owa-accounts-store';
import { lazyShowAdminInstalledNewAddinFRECallout } from 'owa-addins-exp-ui';
import { ExtensibilityModeEnum } from 'owa-addins-types';
import { PerformanceDatapoint } from 'owa-analytics';
import { AddinSidePane, createAppointmentComposeAdapter } from 'owa-calendar-addins';
import { getCalendarIdByFolderId } from 'owa-calendar-cache';
import initializeMailTips from 'owa-calendar-compose-form-actions/lib/actions/initializeMailTips';
import { FullComposeViewState } from 'owa-calendar-compose-types';
import { getComposeCalendarColorScheme } from 'owa-calendar-compose-form-store/lib/selectors/getComposeCalendarColorScheme';
import getFullComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getFullComposeViewState';
import isInPopOutWindow from 'owa-calendar-compose-form-store/lib/selectors/isInPopOutWindow';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import { isEventAMeeting } from 'owa-calendar-event-capabilities';
import { FormsCommandBar } from 'owa-calendar-forms-command-bar';
import CalendarBar from 'owa-calendar-forms-common/lib/components/CalendarBar';
import { getCommands } from 'owa-calendar-hotkeys';
import { lazyInitRoomPreferencesSetting } from 'owa-calendar-room-preferences-option';
import { SchedulingAssistant } from 'owa-calendar-scheduling-assistant';
import SidePane from 'owa-calendar-sidepane/lib/component/SidePane';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { ControlIcons } from 'owa-control-icons';
import Droppable from 'owa-dnd/lib/components/Droppable';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { AccCheckerPane } from 'owa-editor-acc-checker';
import updateContentToViewState from 'owa-editor/lib/utils/updateContentToViewState';
import expressionStore from 'owa-expressions-store/lib/store/expressionStore';
import { ExpressionPane } from 'owa-expressions-view';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { useGlobalHotkey } from 'owa-hotkeys';
import loc from 'owa-localize';
import { closeText } from 'owa-locstrings/lib/strings/closetext.locstring.json';
import { popoutsOpenInNewWindow_0 } from 'owa-locstrings/lib/strings/popoutsopeninnewwindow_0.locstring.json';
import { getOpxHostApi } from 'owa-opx';
import { Room, RoomSelector } from 'owa-room-selection';
import LaunchEventType from 'owa-service/lib/contract/LaunchEventType';
import * as React from 'react';
import {
    expressionsHeader,
    accessibilityCheckerHeader,
    schedulingAssistantHeader,
} from './FullComposeForm.locstring.json';
import {
    setTypeOfItemBeingDragged,
    setShowDropHint,
} from 'owa-calendar-compose-form-store/lib/mutators/fullcomposeviewstate/dragAndDropMutators';
import {
    presentDiscardClosureConfirmation,
    onRoomAdded,
    popOutCompose,
    updateSidePaneType,
    verifyEventThenSave,
    increaseTimeSpentInRoomBookingPanel,
    updateAllDay,
} from 'owa-calendar-compose-form-actions';
import {
    lazyInitializeAddinsForItem,
    lazyOnLaunchEventTriggered,
    getComposeHostItemIndex,
    lazyDeinitializeAddinsForItem,
    lazyOnComposeClose,
    getSurfaceNewAddinId,
    getExtensionForAddinId,
} from 'owa-addins-core';

let styles = require('./FullComposeForm.scss');
let classNames = require('classnames/bind').bind(styles);
let roomPanelPerformanceDatapoint = null;

export default observer(function FullComposeForm() {
    const leftPane = React.useRef<FullComposeLeftPaneHandle>();

    let { event, originalEvent } = getComposeStore();
    const hostItemIndex = event && getComposeHostItemIndex(event.ParentFolderId.Id);
    const fullComposeViewState = getFullComposeViewState();
    const calendarColorScheme: ColorScheme = getComposeCalendarColorScheme();
    const showFullScreen =
        isInPopOutWindow() || isHostAppFeatureEnabled('fullscreenComposeFormPopOut');

    React.useEffect(() => {
        // Send message in opx scenarios that the component is loaded
        if (isHostAppFeatureEnabled('opxComponentLifecycle')) {
            getOpxHostApi().sendPLTDataPoint?.('ComposeForm');
        }
    }, []);

    React.useEffect((): void | (() => void) => {
        const adapter = createAppointmentComposeAdapter();

        if (adapter) {
            const addinSetupPromise = lazyInitializeAddinsForItem
                .import()
                .then(initializeAddinsForItem => {
                    initializeAddinsForItem(
                        hostItemIndex,
                        adapter,
                        false /* renderTaskPaneInPanel */
                    ).then(
                        // trigger lazyOnLaunchEventTriggered action once AppointmentComposeAdapter is created and addins are initialized.
                        () => {
                            const eventFromStore = getComposeStore().event;
                            if (
                                isFeatureEnabled('addin-autoRun') &&
                                isFeatureEnabled('addin-autoRun-newComposeEvent') &&
                                eventFromStore &&
                                !eventFromStore.ItemId?.Id
                            ) {
                                lazyOnLaunchEventTriggered.importAndExecute(
                                    hostItemIndex,
                                    LaunchEventType.OnNewAppointmentOrganizer
                                );
                            }

                            if (isFeatureEnabled('addin-exp-pinNewAdminAddinsFRECallout')) {
                                // One of the treatment in experiment to pin newly installed admin addins
                                // and show lightning nudge for latest addin.
                                window.requestAnimationFrame(() => {
                                    showLatestNewAdminAddinFRECallout();
                                });
                            }
                        }
                    );
                });

            return () => {
                addinSetupPromise.then(() => deinitializeAddinsForCalendarFolder(hostItemIndex));
            };
        }
    }, [hostItemIndex]);

    React.useEffect(() => {
        // initialize preferred room list if the room booking assistant flight is on
        if (
            isFeatureEnabled('cal-mf-roomBookingAssistant') &&
            !isConnectedAccount(getComposeStore().event.ParentFolderId.mailboxInfo.userIdentity)
        ) {
            lazyInitRoomPreferencesSetting.importAndExecute();
        }
    }, []);

    React.useEffect(() => {
        // initialize mailtips for already present recipients
        initializeMailTips();
    }, []);

    React.useEffect(
        () => () => {
            lazyOnComposeClose
                .import()
                .then(onComposeClose => {
                    onComposeClose(hostItemIndex);
                    return lazyDeinitializeAddinsForItem.import();
                })
                .then(deinitializeAddinsForItem => {
                    deinitializeAddinsForItem(hostItemIndex);
                });
        },
        []
    );

    const verifyAndSave = React.useCallback(() => {
        const fullComposeViewState = getFullComposeViewState();
        updateContentToViewState(fullComposeViewState?.bodyViewState);

        verifyEventThenSave();
    }, []);

    useGlobalHotkey(getCommands().saveAppointment, verifyAndSave, {
        allowHotkeyOnTextFields: true,
        isEnabled: () => {
            return (
                !getFullComposeViewState().showSchedulingAssistant &&
                !isEventAMeeting(getComposeStore().event)
            );
        },
    });
    useGlobalHotkey(getCommands().sendMeeting, verifyAndSave, {
        allowHotkeyOnTextFields: true,
        isEnabled: () => {
            return (
                !getFullComposeViewState().showSchedulingAssistant &&
                isEventAMeeting(getComposeStore().event)
            );
        },
    });

    const onDrop = React.useCallback((dragInfo: DragData) => {
        setDataItemType(null);
        setShowDropHint(false);
    }, []);

    const onDragLeave = React.useCallback(() => {
        setDataItemType(null);
        setShowDropHint(false);
    }, []);

    const onDragOver = React.useCallback((dragInfo: DragData) => {
        setDataItemType(dragInfo.itemType);
        setShowDropHint(true);
    }, []);

    const onDismissContextMenu = React.useCallback((): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            window.requestAnimationFrame(() => {
                if (leftPane.current) {
                    leftPane.current.focusToCharmPicker();
                }
                return resolve();
            });
        });
    }, []);

    if (!event || !fullComposeViewState) {
        return null;
    }

    return (
        <>
            <Droppable
                dropViewState={createDropViewState()}
                onDrop={onDrop}
                onDragLeave={onDragLeave}
                shouldIgnoreTransientOnDragLeave={true}
                bypassActOnDrop={true}
                onDragOver={onDragOver}>
                <div
                    className={classNames(styles.container, styles.contentWidthLimits, {
                        showFullScreen: showFullScreen,
                        disableDarkMode:
                            !fullComposeViewState.showSchedulingAssistant &&
                            !fullComposeViewState.isDarkMode,
                    })}>
                    {isHostAppFeatureEnabled('composeFormHeader') &&
                        calendarHeaderElement(calendarColorScheme, showFullScreen)}
                    {fullComposeViewState.showSchedulingAssistant ? (
                        <SchedulingAssistant
                            className={!showFullScreen ? styles.contentWidthLimits : ''}
                        />
                    ) : (
                        <>
                            {fullComposeViewState.awaitingServiceResponse ? (
                                <Spinner
                                    size={SpinnerSize.small}
                                    theme={themeFromColorScheme(calendarColorScheme)}
                                    className={styles.spinner}
                                />
                            ) : (
                                <FormsCommandBar
                                    filterableItems={getComposeCommandBarItems(
                                        event,
                                        originalEvent,
                                        fullComposeViewState,
                                        onDismissContextMenu,
                                        calendarColorScheme,
                                        !fullComposeViewState.uiEnabled /* disabled */
                                    )}
                                    colorScheme={calendarColorScheme}
                                />
                            )}

                            <FullComposeInfoBar colorScheme={calendarColorScheme} />

                            <div
                                className={classNames(
                                    styles.contentPane,
                                    styles.contentWidthLimits,
                                    {
                                        showFullScreen: showFullScreen,
                                    }
                                )}>
                                <FullComposeLeftPane
                                    className={classNames(styles.column, styles.contentColumn)}
                                    ref={leftPane}
                                />
                                <FullComposeRightPane
                                    className={classNames(styles.column, styles.schedulingColumn)}
                                />
                            </div>
                            {renderSidePane(fullComposeViewState)}
                        </>
                    )}
                </div>
            </Droppable>
        </>
    );
});

function renderExpressionPane() {
    let fullComposeViewState = getFullComposeViewState();
    return (
        <SidePane
            title={loc(expressionsHeader)}
            isOpened={fullComposeViewState.currentSidePaneType == SidePaneType.EmojiPicker}
            onClose={toggleEmojiPicker}>
            <div className={classNames(styles.sidePane, styles.expressionPane, 'customScrollBar')}>
                <ExpressionPane expressionId={fullComposeViewState.expressionId} />
            </div>
        </SidePane>
    );
}

function renderAccessibilityChecker() {
    const fullComposeViewState = getFullComposeViewState();
    const closeChecker = () => {
        updateSidePaneType(SidePaneType.None);
    };
    return (
        <SidePane
            title={loc(accessibilityCheckerHeader)}
            isOpened={fullComposeViewState.accChecker.isOpen}
            onClose={closeChecker}>
            <div className={classNames(styles.sidePane, styles.accCheckerPane)}>
                <AccCheckerPane
                    viewState={fullComposeViewState.accChecker}
                    editorViewState={fullComposeViewState.bodyViewState}
                />
            </div>
        </SidePane>
    );
}

function setDataItemType(newValue: string) {
    if (getFullComposeViewState().typeOfItemBeingDragged != newValue) {
        setTypeOfItemBeingDragged(newValue);
    }
}

function closeRoomListPane(timeElapsed: number) {
    roomPanelPerformanceDatapoint?.invalidate();
    increaseTimeSpentInRoomBookingPanel(timeElapsed);
    updateSidePaneType(SidePaneType.None);
}

function roomAdded(room: Room) {
    roomPanelPerformanceDatapoint?.end();
    return onRoomAdded(room.name, room.email);
}

/**
 * Deintializes addin for the given folder
 */
function deinitializeAddinsForCalendarFolder(hostItemId: string) {
    let fullComposeViewState = getFullComposeViewState();
    if (
        fullComposeViewState?.currentSidePaneType ===
        SidePaneType.Addins /*Check if addin sidepane is active*/
    ) {
        updateSidePaneType(SidePaneType.None);
    }
    lazyDeinitializeAddinsForItem.import().then(deinitializeAddinsForItem => {
        deinitializeAddinsForItem(hostItemId);
    });
}

function renderRoomListPane() {
    let fullComposeViewState = getFullComposeViewState();
    const { event } = getComposeStore();
    let calendarColorScheme: ColorScheme = getComposeCalendarColorScheme();
    if (!roomPanelPerformanceDatapoint || roomPanelPerformanceDatapoint.hasEnded) {
        roomPanelPerformanceDatapoint = new PerformanceDatapoint('RoomPanelRoomSelectionTime');
    }

    // Mark event as all day if user selected the place type to be workspace
    const onPlaceTypeChanged = (isTypeWorkspace: boolean) => {
        if (!fullComposeViewState.isAllDayEventExplicitlySet) {
            updateAllDay(isTypeWorkspace);
        }
    };

    return (
        <FocusTrapZone isClickableOutsideFocusTrap={true} forceFocusInsideTrap={false}>
            <SidePane
                isOpened={fullComposeViewState.currentSidePaneType == SidePaneType.RoomList}
                showHeader={false}>
                <div className={classNames(styles.roomPickerPane, 'customScrollBar')}>
                    <RoomSelector
                        onRoomAdded={roomAdded}
                        onClose={closeRoomListPane}
                        event={event}
                        colorScheme={calendarColorScheme}
                        source="FullCompose"
                        onPlaceTypeChanged={onPlaceTypeChanged}
                    />
                </div>
            </SidePane>
        </FocusTrapZone>
    );
}

function renderSidePane(fullComposeViewState: FullComposeViewState) {
    if (!fullComposeViewState.currentSidePaneType) {
        return null;
    }
    switch (fullComposeViewState.currentSidePaneType) {
        case SidePaneType.Addins:
            return <AddinSidePane />;
        case SidePaneType.EmojiPicker:
            return expressionStore.expressionPickerViewStates.get(fullComposeViewState.expressionId)
                ? renderExpressionPane()
                : null;
        case SidePaneType.RoomList:
            return renderRoomListPane();
        case SidePaneType.Tracking:
            return <TrackingSidePane />;
        case SidePaneType.AccessibilityChecker:
            return fullComposeViewState.accChecker ? renderAccessibilityChecker() : null;
    }
    return null;
}

function calendarHeaderElement(colorScheme: ColorScheme, isInPopoutWindow: boolean): JSX.Element {
    let { event } = getComposeStore();
    let { showSchedulingAssistant } = getFullComposeViewState();
    let calendarId = getCalendarIdByFolderId(event.ParentFolderId.Id);
    if (!calendarId && !isFeatureEnabled('cal-mf-calendarPickerTeams')) {
        // Work Item 58585: Clean code once the teams/channels are added to the cache
        return null;
    }
    let style = {
        root: {
            fontSize: '17px',
            color: colorScheme.accessibleText.calPrimaryBackground,
        },
    };
    let buttons = [];
    if (!showSchedulingAssistant) {
        if (!isInPopoutWindow) {
            buttons.push({
                onClick: () => popOutCompose(),
                iconProps: {
                    iconName: ControlIcons.OpenInNewWindow,
                    styles: style,
                },
                title: loc(popoutsOpenInNewWindow_0),
                ariaLabel: loc(popoutsOpenInNewWindow_0),
            });
            buttons.push({
                onClick: presentDiscardClosureConfirmation,
                iconProps: {
                    iconName: ControlIcons.Cancel,
                    styles: style,
                },
                title: loc(closeText),
                ariaLabel: loc(closeText),
            });
        }
    }

    const schedulingAssistantHeaderText = showSchedulingAssistant
        ? loc(schedulingAssistantHeader)
        : null;

    return (
        <CalendarBar
            className={styles.calendarBar}
            calendarColorScheme={colorScheme}
            buttonProps={buttons}
            calendarId={calendarId}
            hideCalendarName={true}
            schedulingAssistantHeader={schedulingAssistantHeaderText}
        />
    );
}

function showLatestNewAdminAddinFRECallout() {
    let newAddinId = getSurfaceNewAddinId(ExtensibilityModeEnum.AppointmentAttendee);
    if (newAddinId) {
        let newAddinName: string = 'New Add-in';
        const extension = getExtensionForAddinId(newAddinId);
        if (extension) {
            newAddinName = extension.DisplayName;
        }

        lazyShowAdminInstalledNewAddinFRECallout.importAndExecute(
            ExtensibilityModeEnum.AppointmentOrganizer,
            newAddinId,
            newAddinName
        );
    }
}
